import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import { ArrowUp, ExternalLink, FileText, Heart, Loader2, Mountain, Plus, QrCode, Share2 } from 'lucide-react';
import { cragsService } from '../services/crags';
import { climbsService } from '../services/climbs';
import { zonesService } from '../services/zones';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { useFilteredClimbsList } from '../hooks/useClimbFilters';
import FollowersList from '../components/FollowersList';
import LocationHeader from '../components/location/LocationHeader';
import { CollapsibleHeader } from '../components/location/CollapsibleHeader';
import { usePullToReveal } from '../hooks/usePullToReveal';
import { useSwipeTabs } from '../hooks/useSwipeTabs';
import { CragInfoPanel } from '../components/crags/CragInfoPanel';
import { CragSendsList } from '../components/crags/CragSendsList';
import { CragSectorTabs, type CragSectorTab } from '../components/crags/CragSectorTabs';
import { CragSectorTopo } from '../components/crags/CragSectorTopo';
import { CragSectorClimbList } from '../components/crags/CragSectorClimbList';
import { CragTopoFab, type CragFabAction } from '../components/crags/CragTopoFab';
import { CragReportDialog } from '../components/crags/CragReportDialog';
import { CragReportsChip } from '../components/crags/CragReportsChip';
import { CragReportsSheet } from '../components/crags/CragReportsSheet';
import { cragLevelReports, groupReportsByClimb } from '../components/crags/report-status';
import { cragReportsService } from '../services/crag-reports';
import type { CragReport } from '../types/crag-report';
import { ClimbLogActions, type ClimbLogAction } from '../components/climbs/ClimbLogActions';
import { ClimbVideosDrawer } from '../components/climbs/ClimbVideosDrawer';
import ClimbSendsDrawer from '../components/sends/ClimbSendsDrawer';
import { cn } from '../lib/utils';
import { trackEvent } from '../utils/analytics';
import { canPrintCragSheet } from '../components/crag-sheet/sheet-access';
import type { Crag } from '../types/crag';
import { climbTitle } from '../utils/climb-title';
import type { Climb } from '../types/climb';
import type { Zone } from '../types/zone';
import { usePushNudgeDialog } from '../components/push/PushNudgeDialog';
import type { GalleryImage } from '../types/location-images';

/** Shared styling for icon buttons overlaid on the cover image — as on a gym. */
const COVER_OVERLAY_BUTTON = 'h-9 w-9 bg-black/40 border-white/20 text-white hover:bg-black/60 hover:text-white';

/** The tab that collects routes nobody has filed under a sector yet. */
const UNSECTORED = 'none';

/**
 * The crag's own tab, first in the strip and where a fresh visit lands: what
 * the place is, before which corner of it you want to read.
 */
const INFO = 'info';

/**
 * Everything anyone has sent here lately, ahead of the sectors: it answers "is
 * this place still being climbed" without having to open a cliff first.
 */
const SENDS = 'sends';

/**
 * How much of the screen the pinned photo may take. It sticks under the navbar
 * with the routes scrolling below it, so a tall cliff shot left uncapped would
 * leave nothing to scroll.
 */
const TOPO_MAX_HEIGHT = '40dvh';

export default function CragProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [crag, setCrag] = useState<Crag | null>(null);
  const [loading, setLoading] = useState(true);
  const [climbs, setClimbs] = useState<Climb[]>([]);
  const [climbsLoading, setClimbsLoading] = useState(true);
  const [sectors, setSectors] = useState<Zone[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const pushNudge = usePushNudgeDialog('follow_crag');

  // Which sector is open lives in the URL: a topo is the kind of thing that
  // gets pasted to whoever you are going climbing with.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSectorId = searchParams.get('sector');

  // The route whose line is lit on the photo, what the floating button was last
  // asked to do to it, and the two drawers a row opens.
  const [selectedClimbId, setSelectedClimbId] = useState<string | null>(null);
  const [logAction, setLogAction] = useState<ClimbLogAction | null>(null);
  const [sendsClimb, setSendsClimb] = useState<Climb | null>(null);
  const [videosClimb, setVideosClimb] = useState<Climb | null>(null);

  // What has been reported wrong here. One list for the whole page: it puts the
  // triangles on the routes *and* the banner above the tabs, and asking twice
  // would be two answers to one question.
  const [reports, setReports] = useState<CragReport[]>([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  // Which report list is open — one route's, or the crag's own.
  const [reportsSheetClimb, setReportsSheetClimb] = useState<Climb | null>(null);
  const [cragReportsSheetOpen, setCragReportsSheetOpen] = useState(false);

  // Picking a sector folds the crag's own details away, so the topo starts at
  // the top of the screen. A deliberate pull down brings them back.
  const [headerFolded, setHeaderFolded] = useState(false);

  // Track when the crag logo scrolls under the sticky navbar, so the breadcrumb
  // can swap the DivePeaks icon for it — the gym profile's behaviour.
  const [logoCovered, setLogoCovered] = useState(false);
  const logoRef = useRef<HTMLDivElement>(null);

  const currentUserStaff = user?.staff?.find(staff => staff.gym_slug === slug) || null;
  const isStaffMember = currentUserStaff?.status === 'confirmed' &&
    (currentUserStaff?.staff_roles.includes('admin') || currentUserStaff?.staff_roles.includes('editor'));

  const isCragAdmin = currentUserStaff?.status === 'confirmed' && currentUserStaff?.staff_roles.includes('admin');
  const isStaffAdmin = !!isCragAdmin;

  // Adding climbs is wider than editing the crag's own copy: a route setter is
  // exactly the person who bolted the line, so they get the button too.
  const canAddClimbs =
    currentUserStaff?.status === 'confirmed' &&
    currentUserStaff.staff_roles.some(role => ['admin', 'editor', 'routesetter'].includes(role));

  useEffect(() => {
    if (!slug) return;
    let isMounted = true;

    const fetchCrag = async () => {
      setLoading(true);
      try {
        const cragData = await cragsService.getCragBySlug(slug);
        if (isMounted) {
          setCrag(cragData);
          setIsFollowing(cragData.is_following || false);
          // `?ref=sheet` is the code on the printed sheet at the cliff (see
          // pages/crag-sheet.tsx): it says how many visits the sign brings.
          const ref = new URLSearchParams(window.location.search).get('ref');
          trackEvent('crag_viewed', ref ? { crag_slug: slug, ref } : { crag_slug: slug });
        }
      } catch (err) {
        if (isMounted) {
          toast({
            title: t('cragProfile.failedToLoad'),
            description: err instanceof Error ? err.message : t('errors.unexpectedError'),
            variant: 'destructive',
          });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCrag();
    return () => { isMounted = false; };
  }, [slug, t, toast]);

  // The sectors, for their photos and their order. The climbs carry only a
  // thumbnail of their zone, and the topo is drawn on the full-size picture.
  useEffect(() => {
    if (!slug) return;
    let isMounted = true;

    zonesService.getZonesForLocation(slug, 'crag')
      .then(zones => { if (isMounted) setSectors(zones); })
      .catch(() => { /* a crag with no sectors reads as one flat list */ });

    return () => { isMounted = false; };
  }, [slug]);

  const fetchClimbs = useCallback(async (cragId: string) => {
    setClimbsLoading(true);
    try {
      const data = await climbsService.searchClimbs(
        {
          location_id: cragId,
          include_sends: true,
          include_user_attempts: true,
        },
        // This payload says which of these lines *you* have sent, and the
        // endpoint answers `max-age=300, private` without varying on the
        // authorization header — so a copy taken before signing in is handed
        // back for five minutes, with everyone's sends missing from it.
        // Revalidating costs an ETag round trip and keeps the answer the
        // reader's own.
        { cache: 'no-cache' }
      );
      setClimbs(data);
    } catch {
      // silently fail — the list will be empty
    } finally {
      setClimbsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!crag) return;
    fetchClimbs(crag.id);
  }, [crag, fetchClimbs]);

  // Logging a send changes the faces on a row, so the list is re-read. Quiet:
  // the user is looking at the row that changed, not at a spinner.
  const handleLogged = useCallback(() => {
    if (crag) fetchClimbs(crag.id);
  }, [crag, fetchClimbs]);

  const fetchReports = useCallback(async () => {
    if (!slug) return;
    try {
      const { reports: fetched } = await cragReportsService.list(slug);
      setReports(fetched);
    } catch {
      // A crag with no reports and a crag whose reports could not be fetched
      // look the same here on purpose: neither is worth an error over the topo.
    }
  }, [slug]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const reportsByClimb = useMemo(() => groupReportsByClimb(reports), [reports]);
  const cragReports = useMemo(() => cragLevelReports(reports), [reports]);

  // The route whose reports are open, and the ones to show in the sheet. Every
  // report on it, not only the open ones: "this was re-bolted in March" is the
  // answer to the triangle, and it is only in the resolved ones.
  const sheetReports = reportsSheetClimb
    ? reportsByClimb.get(reportsSheetClimb.id) ?? []
    : [];

  const climbsBySector = useMemo(() => {
    const groups = new Map<string, Climb[]>();
    for (const climb of climbs) {
      const key = climb.zone?.id ?? UNSECTORED;
      const group = groups.get(key);
      if (group) group.push(climb);
      else groups.set(key, [climb]);
    }
    return groups;
  }, [climbs]);

  // The crag's own two tabs first, then every sector, then a last one for the
  // routes not filed under any — which would otherwise be invisible here.
  const sectorTabs = useMemo<CragSectorTab[]>(() => {
    const tabs: CragSectorTab[] = [
      { id: INFO, name: t('cragInfo.tab') },
      { id: SENDS, name: t('cragSends.tab') },
    ];

    for (const sector of sectors) {
      tabs.push({
        id: sector.id,
        name: sector.name,
        climbsCount: climbsBySector.get(sector.id)?.length ?? 0,
      });
    }

    const unsectored = climbsBySector.get(UNSECTORED)?.length ?? 0;
    if (unsectored > 0) {
      tabs.push({ id: UNSECTORED, name: t('cragProfile.unsectored'), climbsCount: unsectored });
    }

    return tabs;
  }, [sectors, climbsBySector, t]);

  /** How many tabs are the crag's own rather than a sector's. */
  const OWN_TABS = 2;

  /** Whether there is anything to read besides the crag's own tabs. */
  const hasSectorTabs = sectorTabs.length > OWN_TABS;

  const selectSector = useCallback((sectorId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('sector', sectorId);
    setSearchParams(next);
    setSelectedClimbId(null);
    // Picking a tab is asking to read it: fold the crag's details away and
    // bring the tab up under the navbar, where the topo then stays.
    setHeaderFolded(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [searchParams, setSearchParams]);

  const tabIds = useMemo(() => sectorTabs.map(tab => tab.id), [sectorTabs]);

  // Only a deliberate pull from the top brings the crag's details back — a flick
  // while reading the routes must not throw them back onto the screen.
  const pageAtTop = useCallback(() => window.scrollY <= 4, []);
  const unfoldHeader = useCallback(() => setHeaderFolded(false), []);
  usePullToReveal({ enabled: headerFolded, isAtTop: pageAtTop, onReveal: unfoldHeader });

  // Another crag is a fresh arrival, header and all.
  useEffect(() => {
    setHeaderFolded(false);
  }, [slug]);

  // Write the landing tab into the URL, so what is on screen is what gets
  // pasted. Replaces, so Back does not walk into the bare URL that redirected.
  //
  // Held until the sectors are in: until then the strip is just the info tab,
  // and a link straight to a sector would be "corrected" away before the tab it
  // names has had a chance to exist.
  useEffect(() => {
    if (!hasSectorTabs) return;
    if (selectedSectorId && sectorTabs.some(tab => tab.id === selectedSectorId)) return;

    const next = new URLSearchParams(searchParams);
    next.set('sector', sectorTabs[0].id);
    setSearchParams(next, { replace: true });
  }, [hasSectorTabs, sectorTabs, selectedSectorId, searchParams, setSearchParams]);

  // What is actually on screen. A URL naming a sector that is not there yet —
  // or not there at all — reads as the crag's own tab rather than as nothing.
  const activeTabId = selectedSectorId && sectorTabs.some(tab => tab.id === selectedSectorId)
    ? selectedSectorId
    : INFO;
  const showingInfo = activeTabId === INFO;
  const showingSends = activeTabId === SENDS;
  const showingSector = !showingInfo && !showingSends;
  const selectedSector = sectors.find(sector => sector.id === activeTabId) ?? null;
  // Memoised because it is what the route page is handed to swipe through: a
  // fresh empty array every render would rebuild that handover on each one.
  const sectorClimbs = useMemo(
    () => (showingSector ? climbsBySector.get(activeTabId) ?? [] : []),
    [showingSector, climbsBySector, activeTabId]
  );
  const selectedClimb = sectorClimbs.find(climb => climb.id === selectedClimbId) ?? null;

  // Left and right step through the same strip the tabs are in. Spread over the
  // tab's content below, never over the strip itself — that scrolls sideways.
  const swipeHandlers = useSwipeTabs({ tabIds, activeId: activeTabId, onSelect: selectSector });

  // A route's own page swipes left and right through the list it was opened
  // from — the same session storage a gym's climbs list writes, keyed by the
  // location, which is what ClimbNavigation reads back there. So the sector is
  // handed over in the order it is read here, with the route that was tapped
  // marked as where the reader is: swiping right off the last line of the
  // sector must not land on a route from another cliff.
  const { saveFilteredClimbs } = useFilteredClimbsList(crag?.id);

  const openClimbPage = useCallback((climb: Climb) => {
    const index = sectorClimbs.findIndex(candidate => candidate.id === climb.id);
    saveFilteredClimbs(sectorClimbs, index === -1 ? 0 : index);
    // Crag boulders exist and ride their own segment, as they do in a gym.
    const segment = climb.type === 'Boulder' ? 'boulders' : 'routes';
    navigate(`/crags/${slug}/${segment}/${climb.slug}`);
  }, [sectorClimbs, saveFilteredClimbs, navigate, slug]);

  // Reporting is the one FAB action that is not a log entry — it opens its own
  // form rather than driving ClimbLogActions.
  const handleFabAction = useCallback((kind: CragFabAction) => {
    if (kind === 'report') {
      setReportDialogOpen(true);
      return;
    }
    setLogAction(current => ({ kind, nonce: (current?.nonce ?? 0) + 1 }));
  }, []);

  // A different route means the last action belonged to another line.
  const selectClimb = useCallback((climbId: string) => {
    setSelectedClimbId(climbId);
    setLogAction(null);
  }, []);

  // "Last attempt" on a row opens that route's goes. It selects the route on
  // the way — the actions are mounted for whichever line is lit, and lighting
  // the one being asked about is what the reader means anyway.
  const handleOpenAttempts = useCallback((climb: Climb) => {
    setSelectedClimbId(climb.id);
    setLogAction(current => ({ kind: 'attempts', nonce: (current?.nonce ?? 0) + 1 }));
  }, []);

  const handleToggleFollow = async () => {
    if (!user || !slug) {
      toast({
        title: t('cragProfile.loginRequired'),
        description: t('cragProfile.loginRequiredDescription'),
        variant: 'destructive',
      });
      return;
    }
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await cragsService.unfollowCrag(slug);
        setIsFollowing(false);
        toast({ title: t('cragProfile.unfollowSuccess'), description: t('cragProfile.unfollowSuccessDescription', { name: crag?.name }) });
      } else {
        await cragsService.followCrag(slug);
        setIsFollowing(true);
        toast({ title: t('cragProfile.followSuccess'), description: t('cragProfile.followSuccessDescription', { name: crag?.name }) });
        pushNudge.maybeOpen({ name: crag?.name });
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: crag?.name || t('cragProfile.share'),
          text: t('cragProfile.shareText', { name: crag?.name }),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast({ title: t('cragProfile.linkCopied'), description: t('cragProfile.linkCopiedDescription'), duration: 3000 });
      }
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
      }
    }
  };

  // Observe the crag logo: once it scrolls under the sticky navbar (h-16 = 64px),
  // surface it in the breadcrumb instead of the DivePeaks icon.
  useEffect(() => {
    const el = logoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setLogoCovered(!entry.isIntersecting),
      { rootMargin: '-64px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, crag]);

  const handleRevealHeader = useCallback(() => {
    setHeaderFolded(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // The header is out of sight either because it scrolled away or because a
  // sector folded it. Both are undone by the same tap.
  const headerHidden = logoCovered || headerFolded;

  const cragLogo = crag?.logo_small_url || crag?.logo_url;
  const breadcrumbLeadingIcon = headerHidden ? (
    cragLogo ? (
      <img
        src={cragLogo}
        alt={crag?.name ?? ''}
        className="h-8 w-8 rounded-full object-cover animate-in fade-in zoom-in-95 duration-300"
      />
    ) : (
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted animate-in fade-in zoom-in-95 duration-300">
        <ArrowUp className="h-4 w-4" />
      </span>
    )
  ) : undefined;

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: crag
        ? [{
            label: crag.name,
            leadingIcon: breadcrumbLeadingIcon,
            onClick: headerHidden ? handleRevealHeader : undefined,
          }]
        : [],
      hideHomeLogo: headerHidden,
      loading,
    }),
    [crag?.name, cragLogo, headerHidden, handleRevealHeader, loading]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (loading) {
    return (
      <>
        {/* Full-bleed hero placeholder — spans the full viewport width */}
        <div className="-mt-3 -mx-4 sm:-mx-6">
          <Skeleton className="w-full aspect-[16/6]" />
        </div>
        <div className="max-w-4xl mx-auto pt-4 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-10 w-32" />
        </div>
      </>
    );
  }

  if (!crag) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Card><CardContent className="pt-6 text-center"><h1 className="text-2xl font-bold text-muted-foreground">{t('cragProfile.notFound')}</h1></CardContent></Card>
      </div>
    );
  }

  // Staff put the crag's lines up from here — there is no scan step and no
  // kanban outdoors, so the crag's own page is the way in.
  const addClimbMenu = canAddClimbs ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="shrink-0 gap-1.5">
          <Plus className="h-4 w-4" />
          {t('cragProfile.addClimb')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => {
            trackEvent('climb_create_started', { climb_type: 'routes' });
            navigate(`/crags/${slug}/routes/new`);
          }}
        >
          {t('climbCreate.route')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            trackEvent('climb_create_started', { climb_type: 'boulders' });
            navigate(`/crags/${slug}/boulders/new`);
          }}
        >
          {t('climbCreate.boulder')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  return (
    <>
      {/* Full-bleed hero cover, exactly as on a gym: it spans the whole viewport
          width (breaking out of the centered page container), sits flush under
          the navbar, and carries the crag's details as overlays on the picture
          rather than in a card beneath it. The app wrapper clips overflow-x, so
          the breakout never adds a horizontal scrollbar.

          It folds away as soon as a sector is opened — the topo needs the
          screen — and comes back on a pull down or a tap on the breadcrumb. */}
      <CollapsibleHeader collapsed={headerFolded}>
      <Card className="-mt-3 -mx-4 sm:-mx-6 rounded-none border-0 bg-transparent shadow-none">
        <CardContent className="p-0">
          <LocationHeader
            locationId={crag.id}
            name={crag.name}
            bio={crag.bio}
            coverImageUrl={crag.cover_image_url}
            logoUrl={crag.logo_url}
            logoSmallUrl={crag.logo_small_url}
            galleryImages={crag.gallery_images ?? []}
            fallbackIcon={<Mountain className="w-8 h-8 text-muted-foreground" />}
            isStaffMember={!!isStaffMember}
            isStaffAdmin={isStaffAdmin}
            hideName
            logoOverlay
            logoRef={logoRef}
            fullBleed
            coverTopRight={
              <>
                {crag.website_url && (
                  <a
                    href={crag.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-md border border-white/20 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-black/60 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('locationInfo.website')}</span>
                  </a>
                )}
                <Button variant="outline" size="icon" onClick={handleShare} title={t('cragProfile.share')} className={COVER_OVERLAY_BUTTON}>
                  <Share2 className="h-4 w-4" />
                </Button>
                {/* The printable poster — for the board at the parking, or the
                    club's own wall. Only whoever looks after the crag needs it. */}
                {isStaffMember && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/crags/${slug}/qr-poster`)}
                    title={t('qrPoster.button')}
                    className={COVER_OVERLAY_BUTTON}
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                )}
                {/* The routes on A4, to laminate and leave at the foot of the
                    cliff. The same people as the poster, by the same rule as
                    the page it opens. */}
                {slug && canPrintCragSheet(user, slug) && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/crags/${slug}/sheet`)}
                    title={t('cragSheet.button')}
                    aria-label={t('cragSheet.button')}
                    className={COVER_OVERLAY_BUTTON}
                    data-testid="crag-sheet-button"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                )}
              </>
            }
            coverBottomRight={
              <Button
                variant="outline"
                size="icon"
                onClick={handleToggleFollow}
                disabled={followLoading || !user}
                title={isFollowing ? t('cragProfile.removeFromFavorites') : t('cragProfile.addToFavorites')}
                className={COVER_OVERLAY_BUTTON}
              >
                {followLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Heart className={`h-4 w-4 ${isFollowing ? 'fill-red-500 text-red-500' : ''}`} />}
              </Button>
            }
            onNameUpdated={newName => setCrag(c => c ? { ...c, name: newName } : c)}
            onBioUpdated={newBio => setCrag(c => c ? { ...c, bio: newBio } : c)}
            onCoverUpdated={url => setCrag(c => c ? { ...c, cover_image_url: url } : c)}
            onLogoUpdated={(url, smallUrl) => setCrag(c => c ? { ...c, logo_url: url, logo_small_url: smallUrl } : c)}
            onGalleryUpdated={(imgs: GalleryImage[]) => setCrag(c => c ? { ...c, gallery_images: imgs } : c)}
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-6 pt-3 space-y-2">
            {/* Website and share live as overlays on the cover; where the crag
                is belongs to the info tab, next to the parking and the walk-in */}
            {!isFollowing && (
              <div className="flex gap-2 items-center flex-wrap">
                <Button
                  variant="default"
                  onClick={handleToggleFollow}
                  disabled={followLoading || !user}
                  size="sm"
                  className="gap-2"
                >
                  <Heart className="h-4 w-4" />
                  {t('cragProfile.addToFavorites')}
                </Button>
              </div>
            )}

            {/* The followers, and beside them whatever is wrong with the crag
                itself. Last thing before the tabs, which is the last chance to
                catch "the sector is closed this spring" — past here the reader
                is inside one sector and never sees the crag again. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FollowersList
                followers={crag.followers || []}
                totalCount={crag.followers_count}
                followable={{ kind: 'crags', slug: crag.slug }}
              />
              <CragReportsChip
                reports={cragReports}
                onClick={() => setCragReportsSheetOpen(true)}
                className="max-w-full sm:max-w-[60%]"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      </CollapsibleHeader>

      {/* What the crag is, then the topo: pick a sector, read its photo, then
          its routes. */}
      <div className="max-w-4xl mx-auto space-y-4">
        {addClimbMenu && <div className="flex justify-end">{addClimbMenu}</div>}

        {climbsLoading ? (
          <div className="space-y-3">
            <Skeleton className="w-full aspect-[4/3] rounded-lg" />
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* Tabs and photo stick under the navbar (65px: its h-16 plus its
                border) and stay there for as long as there are routes below to
                scroll — which is the point: on a cliff the line on the picture
                and the row describing it are read together, and a topo that
                scrolls away makes you go back up for every route.

                It is capped so the picture cannot eat the screen it is pinned
                to; `-mx` then `px` so the block's background reaches the edges
                of the page and the routes pass under it rather than beside it. */}
            <div className="sticky top-[65px] z-30 -mx-4 space-y-3 bg-background px-4 pb-3 sm:-mx-6 sm:px-6">
              <CragSectorTabs
                sectors={sectorTabs}
                selectedId={activeTabId}
                onSelect={selectSector}
              />

              {/* The crag's own tabs have nothing to pin — their content is
                  read top to bottom rather than against a photo. */}
              {showingSector && (
                selectedSector?.image_url ? (
                  <CragSectorTopo
                    imageUrl={selectedSector.image_url}
                    sectorName={selectedSector.name}
                    climbs={sectorClimbs}
                    selectedClimbId={selectedClimbId}
                    onSelect={selectClimb}
                    imageMaxHeight={TOPO_MAX_HEIGHT}
                  />
                ) : (
                  <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                    {t('cragProfile.sectorHasNoPhoto')}
                  </p>
                )
              )}
            </div>

            {/* Swiping the content steps through the strip above. It is spread
                here rather than on the whole page so the tab strip and the topo
                keep their own gestures. */}
            <div {...swipeHandlers}>
              {showingInfo ? (
                // No card around it: the panel is a stack of cards, one per
                // thing you might be here to read, and a card around cards
                // reads as a box nobody put anything in.
                <div className={cn(user && 'pb-24')}>
                  <CragInfoPanel
                    crag={crag}
                    climbs={climbs}
                    sectors={sectors}
                    onSelectSector={selectSector}
                  />
                </div>
              ) : showingSends ? (
                <Card>
                  <CardContent className={cn('p-0', user && 'pb-24')}>
                    <CragSendsList cragSlug={slug} />
                  </CardContent>
                </Card>
              ) : !hasSectorTabs ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <p className="text-muted-foreground">{t('cragClimbsTable.noClimbs')}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent
                    className={cn(
                      'p-0',
                      // Room for the floating button to sit over, so the last
                      // route can still be scrolled clear of it.
                      user && 'pb-24'
                    )}
                  >
                    <CragSectorClimbList
                      climbs={sectorClimbs}
                      selectedClimbId={selectedClimbId}
                      onSelect={selectClimb}
                      onOpenSends={setSendsClimb}
                      onOpenVideos={setVideosClimb}
                      onOpenAttempts={handleOpenAttempts}
                      onOpenClimb={openClimbPage}
                      reportsByClimb={reportsByClimb}
                      onOpenReports={setReportsSheetClimb}
                      currentUserId={user ? String(user.id) : null}
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>

      {/* One button for the whole topo, pointing at the route that is lit.
          Signed out there is nothing to log against — but with no route lit it
          stays, because reporting the parking or the approach is not about any
          line and must not need one picked first. */}
      {user && (
        <CragTopoFab climb={selectedClimb} onAction={handleFabAction} />
      )}

      {/* Mounted for as long as a route is lit, not just while a dialog is up,
          so the ascent log behind the button is loaded before it is pressed. */}
      {user && selectedClimb && (
        <ClimbLogActions
          key={selectedClimb.id}
          climb={selectedClimb}
          action={logAction}
          dateScope={slug}
          onLogged={handleLogged}
        />
      )}

      {sendsClimb && (
        <ClimbSendsDrawer
          key={sendsClimb.id}
          open={!!sendsClimb}
          onOpenChange={(open) => { if (!open) setSendsClimb(null); }}
          climbSlug={sendsClimb.slug}
          climbName={climbTitle(sendsClimb)}
        />
      )}

      {videosClimb && (
        <ClimbVideosDrawer
          key={videosClimb.id}
          open={!!videosClimb}
          onOpenChange={(open) => { if (!open) setVideosClimb(null); }}
          climbSlug={videosClimb.slug}
          climbName={climbTitle(videosClimb)}
        />
      )}

      {/* Filing one. Prefilled with the lit route when there is one, and about
          the crag itself when there is not. */}
      {user && (
        <CragReportDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          cragSlug={slug}
          cragName={crag.name}
          climbs={climbs}
          defaultClimbId={selectedClimb?.id ?? null}
          onCreated={fetchReports}
        />
      )}

      {/* Reading them: one route's, from its triangle... */}
      <CragReportsSheet
        open={!!reportsSheetClimb}
        onOpenChange={(open) => { if (!open) setReportsSheetClimb(null); }}
        title={reportsSheetClimb?.name ?? ''}
        description={t('cragReports.climbSheetDescription')}
        reports={sheetReports}
      />

      {/* ...and the crag's own, from the banner above the tabs. */}
      <CragReportsSheet
        open={cragReportsSheetOpen}
        onOpenChange={setCragReportsSheetOpen}
        title={crag.name}
        description={t('cragReports.cragSheetDescription')}
        reports={cragReports}
      />

      {pushNudge.dialog}
    </>
  );
}
